import { useRef } from 'react'

// Formata telefone BR a partir de QUALQUER entrada (ignora pontuação que o usuário digitar).
// 01/07 (pedido do usuário): usuário digita só os dígitos; o JS coloca parênteses e traço.
//  celular (9 dígitos / começa com 9) → "(88) 99264-0275"   ·   fixo (8 dígitos) → "(88) 3264-0275"
// Os parênteses são prefixo constante e não atrapalham o cálculo de cursor (posCaret conta só dígitos).
export function formatTelefoneBR(v: string): string {
  const d = (v || '').replace(/\D/g, '').slice(0, 11)
  if (d.length === 0) return ''
  const ddd = d.slice(0, 2)
  if (d.length <= 2) return `(${ddd}`   // enquanto digita o DDD, abre só o parêntese
  const n = d.slice(2)
  const cel = n.length > 8 || /^9/.test(n)   // celular → bloco de 5; fixo → bloco de 4
  const corte = cel ? 5 : 4
  if (n.length <= corte) return `(${ddd}) ${n}`
  return `(${ddd}) ${n.slice(0, corte)}-${n.slice(corte)}`
}

// Posição do cursor no texto formatado, logo após o N-ésimo dígito.
function posCaret(s: string, nDigitos: number): number {
  if (nDigitos <= 0) return 0
  let c = 0
  for (let i = 0; i < s.length; i++) {
    if (/\d/.test(s[i])) { c++; if (c === nDigitos) return i + 1 }
  }
  return s.length
}

type Props = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> & {
  value: string
  onChange: (v: string) => void
}

// Input de telefone que aplica a máscara enquanto digita, SEM quebrar:
//  - aceita o usuário colando/digitando espaço/traço (são ignorados e remarcados);
//  - apagar dígitos funciona normalmente;
//  - apagar um separador apaga o dígito anterior (Backspace intuitivo);
//  - mantém o cursor no lugar certo (não pula pro fim).
export function TelefoneInput({ value, onChange, ...rest }: Props) {
  const ref = useRef<HTMLInputElement>(null)
  const display = formatTelefoneBR(value || '')

  function handle(e: React.ChangeEvent<HTMLInputElement>) {
    const el = e.target
    const selStart = el.selectionStart ?? el.value.length
    const digitosAntes = el.value.slice(0, selStart).replace(/\D/g, '').length
    const apagou = el.value.length < display.length

    let digits = el.value.replace(/\D/g, '').slice(0, 11)
    let dAntes = digitosAntes
    // Backspace que removeu só um separador → remove o dígito imediatamente anterior.
    if (apagou && digits.length === display.replace(/\D/g, '').length && dAntes > 0) {
      digits = digits.slice(0, dAntes - 1) + digits.slice(dAntes)
      dAntes -= 1
    }

    const novo = formatTelefoneBR(digits)
    onChange(novo)
    requestAnimationFrame(() => {
      const pos = posCaret(novo, dAntes)
      try { el.setSelectionRange(pos, pos) } catch { /* noop */ }
    })
  }

  return (
    <input
      ref={ref}
      value={display}
      onChange={handle}
      inputMode="numeric"
      autoComplete="tel"
      placeholder="(88) 99264-0275"
      {...rest}
    />
  )
}
